import { useCallback, useEffect, useState } from "react";
import { BadgeCheck, BookOpen, ShieldCheck } from "lucide-react";
import {
  commercialPolicyStatus,
  acknowledgeCommercialPolicy,
  quoteCommercialTerms,
  type CommercialPolicyStatus,
} from "./lib/api";
import { useCrmIdentity } from "./lib/crmIdentity";

const CANONICAL_DOC_PATH =
  "docs/sales/COMMERCIAL_TERMS_DISCOUNT_AND_COMMISSION_POLICY.md";

const AUTHORITY_LINES: ReadonlyArray<readonly [string, string]> = [
  ["commercial.discount.request", "Request discounts up to the standard maximum"],
  ["commercial.discount.approve.tier1", "Approve Tier 1 discount requests"],
  ["commercial.discount.approve.tier2", "Approve Tier 2 discount requests"],
  [
    "commercial.discount.approve.executive",
    "Exercise executive approval authority, including recorded overrides",
  ],
  ["commercial.payment.issue", "Issue attributed checkout and payment links"],
  [
    "commercial.agreement.request",
    "Request case study participation agreements",
  ],
  [
    "commercial.agreement.manage",
    "Manage custom and case study agreements",
  ],
  ["commercial.agreement.approve", "Approve custom commercial agreements"],
];

export function SalesKnowledge() {
  const identity = useCrmIdentity();
  const [status, setStatus] = useState<CommercialPolicyStatus | null>(null);
  const [baseCommissionBps, setBaseCommissionBps] = useState<number | null>(
    null,
  );
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [acknowledging, setAcknowledging] = useState(false);

  const refresh = useCallback(() => {
    const requests: Promise<void>[] = [
      commercialPolicyStatus()
        .then((value) => setStatus(value))
        .catch(() => undefined),
      quoteCommercialTerms({ plan: "watch", locations: 1, discountBps: 0 })
        .then((quote) => setBaseCommissionBps(quote.baseCommissionBps))
        .catch(() => undefined),
    ];
    Promise.all(requests).then(() => setLoadState("ready"));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function acknowledge() {
    if (acknowledging) return;
    setAcknowledging(true);
    try {
      await acknowledgeCommercialPolicy();
      refresh();
    } catch {
      // Truthful failure: the acknowledgement banner stays until the server confirms.
    } finally {
      setAcknowledging(false);
    }
  }

  return (
    <div className="page sales-knowledge">
      <div className="page-heading">
        <div>
          <span className="eyebrow">SALES KNOWLEDGE</span>
          <h1>Sales Knowledge</h1>
          <p>
            The governing commercial policy for every close made through this
            CRM. The platform enforces these numbers; if the platform and this
            page ever disagree, stop and escalate rather than improvising.
          </p>
        </div>
        <BookOpen size={26} />
      </div>

      {loadState === "loading" && (
        <section className="panel">
          <p>Loading policy status…</p>
        </section>
      )}
      {loadState === "ready" && !status && (
        <section className="panel">
          <p role="alert">
            Policy status is unavailable right now. No policy facts are inferred
            while the server cannot be reached.
          </p>
        </section>
      )}
      {status && (
        <>
          <section className="panel policy-status-panel">
            <div className="policy-status-grid">
              <div className="policy-version-badge">
                POLICY VERSION {status.policyVersion}
              </div>
              <div>
                <span>Effective</span>
                <strong>{new Date(status.effectiveAt).toLocaleDateString()}</strong>
              </div>
              <div>
                <span>Your effective base commission</span>
                <strong>
                  {baseCommissionBps != null
                    ? `${(baseCommissionBps / 100).toFixed(2)}%`
                    : "Unknown"}
                </strong>
              </div>
              <div>
                <span>Standard max discount</span>
                <strong>{(status.standardMaxBps / 100).toFixed(1)}%</strong>
              </div>
              <div>
                <span>Approval window</span>
                <strong>{status.approvalTtlHours} hours</strong>
              </div>
            </div>
            <div className="authorities">
              <h3>YOUR AUTHORITIES</h3>
              <ul>
                {AUTHORITY_LINES.filter(
                  ([capability]) =>
                    (identity.capabilities as readonly string[]).includes(
                      capability,
                    ),
                ).map(([capability, line]) => (
                  <li key={capability}>
                    <ShieldCheck size={14} /> {line}
                  </li>
                ))}
                {AUTHORITY_LINES.every(
                  ([capability]) =>
                    !(identity.capabilities as readonly string[]).includes(
                      capability,
                    ),
                ) && <li>No commercial authorities are held by this role.</li>}
              </ul>
            </div>
          </section>

          {!status.acknowledged ? (
            <section className="panel ack-required-banner" role="status">
              <p>
                I have read and understand the Commercial Terms, Discount &
                Commission Policy.
              </p>
              <button
                type="button"
                className="primary-btn"
                disabled={acknowledging}
                onClick={() => void acknowledge()}
              >
                <BadgeCheck size={15} />
                {acknowledging ? "Recording…" : "Acknowledge"}
              </button>
            </section>
          ) : (
            <p className="ack-recorded" role="status">
              Acknowledged for policy version {status.policyVersion}.
            </p>
          )}
        </>
      )}

      <article className="policy-article">
        <section className="panel">
          <h2>Default and individual commission</h2>
          <p>
            List pricing per location, billed monthly by the Owner product (USD):
            Watch $79 first location / $39 each additional; Protect $149 / $99;
            Shield $299 / $219. A deal's list total is the first-location price
            plus the additional-location price times (locations minus one).
          </p>
          <table>
            <thead>
              <tr>
                <th>Plan</th>
                <th>First location</th>
                <th>Each additional location</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Watch</td>
                <td>$79</td>
                <td>$39</td>
              </tr>
              <tr>
                <td>Protect</td>
                <td>$149</td>
                <td>$99</td>
              </tr>
              <tr>
                <td>Shield</td>
                <td>$299</td>
                <td>$219</td>
              </tr>
            </tbody>
          </table>
          <p>
            Default commission: <strong>25%</strong> of the deal's list total,
            calculated on list price. An active governed participant override
            supersedes that default for the named participant. Commission never
            goes below zero.
          </p>
        </section>

        <section className="panel">
          <h2>How discounts affect your commission</h2>
          <p>
            Discount sharing is <strong>50/50</strong>: your commission is
            reduced by exactly half of whatever discount the customer receives.
            Worked example — Protect at one location with a 10% discount:
          </p>
          <table>
            <thead>
              <tr>
                <th>Figure</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>List price (Protect, one location)</td>
                <td>$149.00/mo</td>
              </tr>
              <tr>
                <td>10% discount</td>
                <td>-$14.90</td>
              </tr>
              <tr>
                <td>Customer pays</td>
                <td>$134.10/mo</td>
              </tr>
              <tr>
                <td>Base commission on list (25%)</td>
                <td>$37.25</td>
              </tr>
              <tr>
                <td>Your share of the discount (50%)</td>
                <td>-$7.45</td>
              </tr>
              <tr>
                <td>Adjusted commission</td>
                <td>$29.80</td>
              </tr>
              <tr>
                <td>Effective rate on what the customer pays</td>
                <td>22.22%</td>
              </tr>
              <tr>
                <td>Company retained</td>
                <td>$104.30/mo</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className="panel">
          <h2>Maximum standard discount</h2>
          <p>
            The hard maximum standard discount is <strong>20% off list</strong>.
            Nothing above 20% can be quoted or issued through standard terms,
            regardless of role. Terms above the maximum require a Custom
            Commercial Agreement with explicit executive activation.
          </p>
        </section>

        <section className="panel">
          <h2>Approval ladder</h2>
          <p>
            Discounts are approved by authority level, not by negotiation. The
            tier required for a given request is returned by the quoting engine;
            it is never guessed in the interface.
          </p>
          <table>
            <thead>
              <tr>
                <th>Max discount</th>
                <th>Required approval</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>up to 5%</td>
                <td>Tier 1 — Sales Manager</td>
              </tr>
              <tr>
                <td>up to 10%</td>
                <td>Tier 2 — Sales Admin</td>
              </tr>
              <tr>
                <td>up to 15%</td>
                <td>Executive — Super Admin</td>
              </tr>
              <tr>
                <td>up to 20%</td>
                <td>Executive — Super Admin</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className="panel">
          <h2>Programs</h2>
          <p>
            Program discounts stack within the same 20% hard maximum. Each
            program carries its own ceiling on the active policy version:
          </p>
          <table>
            <thead>
              <tr>
                <th>Program</th>
                <th>Maximum</th>
                <th>Standard commitment</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Case Study Partner</td>
                <td>10%</td>
                <td>12 months</td>
              </tr>
              <tr>
                <td>Early Adopter</td>
                <td>10%</td>
                <td>—</td>
              </tr>
              <tr>
                <td>Multi-Location</td>
                <td>10%</td>
                <td>—</td>
              </tr>
              <tr>
                <td>Strategic Account</td>
                <td>15%</td>
                <td>—</td>
              </tr>
              <tr>
                <td>Retention Recovery</td>
                <td>10%</td>
                <td>—</td>
              </tr>
              <tr>
                <td>Executive Approved</td>
                <td>20%</td>
                <td>—</td>
              </tr>
            </tbody>
          </table>
          <h3>Case Study Partner rules</h3>
          <p>
            Case Study Partner requires a signed case study agreement —
            participation and data use both agreed — before any discounted quote
            can be requested. Publicity permissions are separated: name, logo,
            testimonial, and quote each carry their own recorded grant state, so
            agreeing to participate never implies publicity rights.
          </p>
        </section>

        <section className="panel">
          <h2>No verbal approvals · No self-approval</h2>
          <p>
            Every discount must exist as an approval request in the platform
            with a recorded decision; a manager saying yes out loud is not an
            approval, so never issue against one. The person who requests a
            discount can never decide its approval — even for their own team,
            even temporarily. An executive may override their own request only
            with a written reason of meaningful length, and that override, its
            author, and its reason are permanently recorded and visible to
            audit.
          </p>
        </section>

        <section className="panel">
          <h2>Terms lock &amp; invalidation</h2>
          <p>
            Every quoted deal is bound to a canonical terms fingerprint. If
            plan, locations, program, discount, duration, or policy version
            change after an approval is issued, the approval stops working and
            checkout against stale terms is denied — re-request under the new
            terms instead. Approvals also expire when their window closes;
            expired requests must be re-requested, not revived.
          </p>
        </section>

        <section className="panel">
          <h2>Strategic accounts &amp; custom or agency agreements</h2>
          <p>
            Strategic Account pricing above the program ceiling follows the
            executive path like any other out-of-band term. Custom agreements
            (negotiated per location, flat recurring, annual prepaid, or custom
            quote) are not percent-off-retail deals: they define their own
            pricing and commitment, require explicit executive activation, and
            their commission treatment — including no commission — must be
            explicitly configured before activation. A custom agreement never
            silently inherits the standard discount formula. Agency arrangements
            live under the same agreement discipline; they are distinct product
            surfaces and never merge CRM ownership.
          </p>
        </section>

        <section className="panel">
          <h2>Refunds &amp; disputes</h2>
          <p>
            Commission follows collected revenue evidence. When a payment is
            refunded, disputed, or fails after commission accrued at a
            configured rate, the accrual is corrected in line with the verified
            billing record. Do not expect commission on revenue the company does
            not collect, and treat a reported repair as unrecovered until the
            provider evidence shows recovery.
          </p>
        </section>

        <section className="panel">
          <h2>Approval statuses</h2>
          <dl className="status-glossary">
            <div>
              <dt>PENDING APPROVAL</dt>
              <dd>Waiting on an authority decision inside its expiry window.</dd>
            </div>
            <div>
              <dt>APPROVED</dt>
              <dd>
                Decided in favor; usable once for checkout before expiry or use.
              </dd>
            </div>
            <div>
              <dt>CHANGES REQUESTED</dt>
              <dd>The approver asked for different terms; re-request.</dd>
            </div>
            <div>
              <dt>REJECTED</dt>
              <dd>Decided against; do not issue.</dd>
            </div>
            <div>
              <dt>EXPIRED</dt>
              <dd>Window closed without issuance; re-request fresh.</dd>
            </div>
            <div>
              <dt>REVOKED</dt>
              <dd>Withdrawn by authority after approval; unusable.</dd>
            </div>
            <div>
              <dt>CONSUMED</dt>
              <dd>Used to issue a checkout session; single-use only.</dd>
            </div>
          </dl>
        </section>

        <section className="panel">
          <h2>Who to contact</h2>
          <p>
            Tier 1 questions go to your Sales Manager; Tier 2 to the Sales
            Admin; executive ceilings, overrides, and anything this page seems
            to contradict go to a Super Admin. When the platform and this
            document disagree, stop and escalate rather than improvising.
          </p>
        </section>

        <footer className="policy-footer">
          Canonical source document:{" "}
          <code>{CANONICAL_DOC_PATH}</code>. This article describes active
          policy version {status?.policyVersion ?? "…"}; pricing or formula
          changes ship as new immutable versions, never edits.
        </footer>
      </article>
    </div>
  );
}
